import React, { lazy, Suspense } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { RouteSeo } from './lib/seo';
import Header from './components/Header';
import NotificationBanner from './components/NotificationBanner';
import { Toaster } from './components/ui/sonner';
import { usePageTracking } from './hooks/usePageTracking';
import { installAdminAuthInterceptor } from './lib/adminAuth';

// Attach the Authorization: Bearer header to every admin axios call, so admin
// auth still works even when the httpOnly cookie isn't sent (e.g. cross-origin
// SameSite=Lax cases in production).
installAdminAuthInterceptor();

// Eagerly load Home for fast first paint
import Home from './pages/Home';

// Lazy load all other pages for faster initial load
const About = lazy(() => import('./pages/About'));
const Beliefs = lazy(() => import('./pages/Beliefs'));
const WhatToExpect = lazy(() => import('./pages/WhatToExpect'));
const Ministries = lazy(() => import('./pages/Ministries'));
const Connect = lazy(() => import('./pages/Connect'));
const BibleStudy = lazy(() => import('./pages/BibleStudy'));
const Team = lazy(() => import('./pages/Team'));
const WatchLive = lazy(() => import('./pages/WatchLive'));
const Sermons = lazy(() => import('./pages/Sermons'));
const PrayerFasting = lazy(() => import('./pages/PrayerFasting'));
const MinistryMicrosite = lazy(() => import('./pages/MinistryMicrosite'));
const PastorPage = lazy(() => import('./pages/PastorPage'));
const ConductAgreement = lazy(() => import('./pages/ConductAgreement'));
const PastorCorner = lazy(() => import('./pages/PastorCorner'));
const Devotional = lazy(() => import('./pages/Devotional'));
const SafeChurch = lazy(() => import('./pages/SafeChurch'));
const PlanYourVisit = lazy(() => import('./pages/PlanYourVisit'));

// Route wrapper: hides pages when admins toggle them off in /admin/site-pages
import PageGuard from './components/PageGuard';
import ScrollToTop from './components/ScrollToTop';

// Admin Pages - lazy loaded
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const AdminResetPassword = lazy(() => import('./pages/admin/AdminResetPassword'));
const AdminLayout = lazy(() => import('./pages/admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminPrayerRequests = lazy(() => import('./pages/admin/AdminPrayerRequests'));
const AdminConnections = lazy(() => import('./pages/admin/AdminConnections'));
const AdminContent = lazy(() => import('./pages/admin/AdminContent'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminMinistries = lazy(() => import('./pages/admin/AdminMinistries'));
const AdminMedia = lazy(() => import('./pages/admin/AdminMedia'));
const AdminServices = lazy(() => import('./pages/admin/AdminServices'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const AdminPageBuilder = lazy(() => import('./pages/admin/AdminPageBuilder'));
const AdminBibleStudy = lazy(() => import('./pages/admin/AdminBibleStudy'));
const AdminConduct = lazy(() => import('./pages/admin/AdminConduct'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSermons = lazy(() => import('./pages/admin/AdminSermons'));
const AdminPastorCorner = lazy(() => import('./pages/admin/AdminPastorCorner'));
const AdminDevotionals = lazy(() => import('./pages/admin/AdminDevotionals'));
const AdminPrayerFasting = lazy(() => import('./pages/admin/AdminPrayerFasting'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminPastorMicrosite = lazy(() => import('./pages/admin/AdminPastorMicrosite'));
const AdminPages = lazy(() => import('./pages/admin/AdminPages'));
const AdminYouTube = lazy(() => import('./pages/admin/AdminYouTube'));
const AdminLiveStream = lazy(() => import('./pages/admin/AdminLiveStream'));
const AdminInstagram = lazy(() => import('./pages/admin/AdminInstagram'));
const AdminAuditLog = lazy(() => import('./pages/admin/AdminAuditLog'));
const AdminEvents = lazy(() => import('./pages/admin/AdminEvents'));
const AdminPlannedVisits = lazy(() => import('./pages/admin/AdminPlannedVisits'));
const AdminPlanVisitPage = lazy(() => import('./pages/admin/AdminPlanVisitPage'));
const AdminHostList = lazy(() => import('./pages/admin/AdminHostList'));

// Page tracking wrapper component
const PageTracker = ({ children }) => {
  usePageTracking();
  return children;
};

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-white">
    <div className="w-8 h-8 border-4 border-green-200 border-t-green-600 rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <PageTracker>
          <ScrollToTop />
          <RouteSeo />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<><Header /><Home /></>} />
              <Route path="/about" element={<><Header /><PageGuard slug="about"><About /></PageGuard></>} />
              <Route path="/beliefs" element={<><Header /><PageGuard slug="beliefs"><Beliefs /></PageGuard></>} />
              <Route path="/plan-your-visit" element={<><Header /><PageGuard slug="plan-your-visit"><PlanYourVisit /></PageGuard></>} />
              <Route path="/what-to-expect" element={<><Header /><PageGuard slug="what-to-expect"><WhatToExpect /></PageGuard></>} />
              <Route path="/ministries" element={<><Header /><PageGuard slug="ministries"><Ministries /></PageGuard></>} />
              <Route path="/connect" element={<><Header /><PageGuard slug="connect"><Connect /></PageGuard></>} />
              <Route path="/bible-study" element={<><Header /><PageGuard slug="bible-study"><BibleStudy /></PageGuard></>} />
              <Route path="/team" element={<><Header /><PageGuard slug="team"><Team /></PageGuard></>} />
              <Route path="/watch-live" element={<><Header /><PageGuard slug="watch-live"><WatchLive /></PageGuard></>} />
              <Route path="/sermons" element={<><Header /><PageGuard slug="sermons"><Sermons /></PageGuard></>} />
              <Route path="/pastor-corner" element={<><Header /><PageGuard slug="pastor-corner"><PastorCorner /></PageGuard></>} />
              <Route path="/devotional" element={<><Header /><Devotional /></>} />
              <Route path="/devotional/:slug" element={<><Header /><Devotional /></>} />
              <Route path="/ministries/prayer-fasting" element={<><Header /><PageGuard slug="prayer-fasting"><PrayerFasting /></PageGuard></>} />
              <Route path="/ministries/flourish/safe-church" element={<Navigate to="/ministries/restore/safe-church" replace />} />
              <Route path="/ministries/restore/safe-church" element={<><Header /><PageGuard slug="safe-church"><SafeChurch /></PageGuard></>} />
              <Route path="/ministries/:slug" element={<><Header /><MinistryMicrosite /></>} />
              <Route path="/team/pastor-andrew" element={<><Header /><PastorPage /></>} />
              <Route path="/conduct" element={<ConductAgreement />} />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
              <Route path="/admin/reset-password/:token" element={<AdminResetPassword />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="prayer-requests" element={<AdminPrayerRequests />} />
                <Route path="connections" element={<AdminConnections />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="team" element={<AdminTeam />} />
                <Route path="ministries" element={<AdminMinistries />} />
                <Route path="media" element={<AdminMedia />} />
                <Route path="services" element={<AdminServices />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="pages" element={<AdminPageBuilder />} />
                <Route path="bible-study" element={<AdminBibleStudy />} />
                <Route path="conduct" element={<AdminConduct />} />
                <Route path="sermons" element={<AdminSermons />} />
                <Route path="pastor-corner" element={<AdminPastorCorner />} />
                <Route path="devotionals" element={<AdminDevotionals />} />
                <Route path="prayer-fasting" element={<AdminPrayerFasting />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="ministry-microsites" element={<Navigate to="/admin/ministries" replace />} />
                <Route path="pastor-microsite" element={<AdminPastorMicrosite />} />
                <Route path="site-pages" element={<AdminPages />} />
                <Route path="youtube" element={<AdminYouTube />} />
                <Route path="livestream" element={<AdminLiveStream />} />
                <Route path="instagram" element={<AdminInstagram />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="events" element={<AdminEvents />} />
                <Route path="audit-log" element={<AdminAuditLog />} />
                <Route path="planned-visits" element={<AdminPlannedVisits />} />
                <Route path="planned-visits/host-list" element={<AdminHostList />} />
                <Route path="plan-visit-page" element={<AdminPlanVisitPage />} />
              </Route>
            </Routes>
          </Suspense>
        </PageTracker>
      </BrowserRouter>
      <Toaster />
      <NotificationBanner />
    </div>
  );
}

export default App;
